import { useCallback, useEffect, useRef, useState } from 'react';

import {
  isAndroidUserAgent,
  isDismissCooldownActive,
  isIosLike,
  isMobileOrTabletContext,
  isStandaloneDisplayMode,
  PWA_PROMOTE_DELAY_MS,
  resolveInstallPath,
  writeDismissCooldown,
} from '../utils/pwaInstall';

/**
 * Owns deferred beforeinstallprompt event, standalone detection, and eligibility.
 * Does not store any auth/PII state.
 */
export default function usePwaInstall({ delayMs = PWA_PROMOTE_DELAY_MS } = {}) {
  const deferredPromptRef = useRef(null);
  const [hasDeferredPrompt, setHasDeferredPrompt] = useState(false);
  const [isStandalone, setIsStandalone] = useState(() => isStandaloneDisplayMode());
  const [wasInstalledThisSession, setWasInstalledThisSession] = useState(false);
  const [cooldownActive, setCooldownActive] = useState(() => isDismissCooldownActive());
  const [shellReady, setShellReady] = useState(false);
  const [mobileContext, setMobileContext] = useState(() => isMobileOrTabletContext());
  const [iosLike, setIosLike] = useState(() => isIosLike());
  const [androidUa, setAndroidUa] = useState(() => isAndroidUserAgent());

  useEffect(() => {
    setMobileContext(isMobileOrTabletContext());
    setIosLike(isIosLike());
    setAndroidUa(isAndroidUserAgent());
    setIsStandalone(isStandaloneDisplayMode());
    setCooldownActive(isDismissCooldownActive());
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => setShellReady(true), delayMs);
    return () => window.clearTimeout(timer);
  }, [delayMs]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    let media;
    try {
      media = window.matchMedia('(display-mode: standalone)');
    } catch (_err) {
      return undefined;
    }

    const syncStandalone = () => {
      setIsStandalone(isStandaloneDisplayMode());
    };

    syncStandalone();

    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', syncStandalone);
      return () => media.removeEventListener('change', syncStandalone);
    }
    if (typeof media.addListener === 'function') {
      media.addListener(syncStandalone);
      return () => media.removeListener(syncStandalone);
    }
    return undefined;
  }, []);

  useEffect(() => {
    const onBeforeInstallPrompt = (event) => {
      event.preventDefault();
      deferredPromptRef.current = event;
      setHasDeferredPrompt(true);
    };

    const onAppInstalled = () => {
      deferredPromptRef.current = null;
      setHasDeferredPrompt(false);
      setWasInstalledThisSession(true);
      setIsStandalone(true);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  const installPath = resolveInstallPath({
    hasDeferredPrompt,
    iosLike,
    mobile: mobileContext,
    androidUa,
  });

  const isInstalled = isStandalone || wasInstalledThisSession;

  const shouldShowPromotion =
    shellReady &&
    mobileContext &&
    !isInstalled &&
    !cooldownActive &&
    installPath != null;

  const dismiss = useCallback(() => {
    writeDismissCooldown();
    setCooldownActive(true);
  }, []);

  const promptInstall = useCallback(async () => {
    const event = deferredPromptRef.current;
    if (!event || typeof event.prompt !== 'function') {
      return { outcome: 'unavailable' };
    }

    // Consume once — clear before awaiting so a second click cannot reuse it.
    deferredPromptRef.current = null;
    setHasDeferredPrompt(false);

    try {
      await event.prompt();
      const choice = await event.userChoice;
      const outcome = choice && choice.outcome ? choice.outcome : 'dismissed';
      if (outcome === 'accepted') {
        setWasInstalledThisSession(true);
        setIsStandalone(true);
      } else {
        writeDismissCooldown();
        setCooldownActive(true);
      }
      return { outcome };
    } catch (_err) {
      writeDismissCooldown();
      setCooldownActive(true);
      return { outcome: 'dismissed' };
    }
  }, []);

  return {
    shouldShowPromotion,
    installPath,
    hasDeferredPrompt,
    isStandalone: isInstalled,
    iosLike,
    dismiss,
    promptInstall,
  };
}
